import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Alert from 'core/components/Alert'
import PaginatedSelectableCards from 'core/components/PaginatedSelectableCards'
import SelectableCard from 'core/components/SelectableCard'
import SizeInputWithUnit, { Unit } from 'core/components/SizeInputWithUnit'
import Info from 'core/components/validatedForm/Info'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Dropdown from 'core/elements/dropdown'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import { minValueValidator, nonDecimalValueValidator } from 'core/utils/fieldValidators'
import { humanReadableSize } from 'openstack/helpers'
import { listFlavors } from '../../flavors/actions'
import { flavorsSelector } from '../../flavors/selectors'
import {
  getFilteredCreateVmFlavors,
  hotplugMemoryMiBValidator,
  hotplugVcpuMaxValidator,
  hotplugVcpuValidator,
  isCreateVmVolumeSourceType,
} from '../CreateVirtualMachineModal.helpers'
import useCreateVirtualMachineModalStyles from '../CreateVirtualMachineModal.styles'
import {
  flavorColumns,
  flavorColumnsGpu,
  isFlavorCompatibleWithImageAndVolume,
  visibleFlavorColumns,
  visibleGpuFlavorColumns,
} from '../helpers'
import ViewModeToggle from './ViewModeToggle'

const ramOptions = [
  { label: 'MiB', value: 'MiB' as Unit },
  { label: 'GiB', value: 'GiB' as Unit },
]

interface FlavorControllerInput {
  params: any
}

interface Props {
  filteredFlavors: any[]
  flavorSort: string
  loadingFlavors: boolean
  params: any
  setFlavorSort: (value: string) => void
  setViewFlavorToggle: (value: string) => void
  updateParams: any
  viewFlavorToggle: string
}

export function useCreateVirtualMachineFlavorStepController({ params }: FlavorControllerInput) {
  const [viewFlavorToggle, setViewFlavorToggle] = useState('list')
  const [flavorSort, setFlavorSort] = useState('')

  const { loading: loadingFlavors } = useListAction(listFlavors, {
    params: {},
  })
  const flavors = useSelector(flavorsSelector)

  const isVolumeSourceType = useMemo(() => isCreateVmVolumeSourceType(params), [params])

  const filteredFlavors = useMemo(
    () =>
      getFilteredCreateVmFlavors({
        flavors,
        flavorSort,
        enableHotplugging: params.enableHotplugging,
        gpuCompatible: params.gpuCompatible,
        isVolumeSourceType,
      }),
    [flavorSort, flavors, isVolumeSourceType, params.enableHotplugging, params.gpuCompatible],
  )

  const resetFlavorStepState = () => {
    setViewFlavorToggle('list')
    setFlavorSort('')
  }

  return {
    filteredFlavors,
    flavorSort,
    flavors,
    isVolumeSourceType,
    loadingFlavors,
    resetFlavorStepState,
    setFlavorSort,
    setViewFlavorToggle,
    viewFlavorToggle,
  }
}

export default function CreateVirtualMachineFlavorStep({
  filteredFlavors,
  flavorSort,
  loadingFlavors,
  params,
  setFlavorSort,
  setViewFlavorToggle,
  updateParams,
  viewFlavorToggle,
}: Props) {
  const classes = useCreateVirtualMachineModalStyles()
  const isVolumeSourceType = useMemo(() => isCreateVmVolumeSourceType(params), [params])

  const renderFlavorCard = useMemo(
    () => (item) => {
      const disabled = isFlavorCompatibleWithImageAndVolume(item, params)

      return (
        <SelectableCard
          id={item}
          onClick={(selectedFlavor) =>
            updateParams({
              flavor: [selectedFlavor],
            })
          }
          tooltip={disabled ? 'Flavor disk size is smaller than the selected image' : undefined}
          active={params.flavor?.[0]?.id === item.id}
          className={classes.card}
          disabled={disabled}
          withCustomBody
          showRadioButton
        >
          <Text variant="body2" className={classes.cardHeader}>
            <b>{item.name}</b>
            {item?.gpuFlavor && <div className={classes.gpuTag}>GPU</div>}
          </Text>
          <div className={classes.pairs}>
            <Text variant="body2" className={classes.cardText}>
              vCPUs: <b>{item?.vcpus}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              RAM: <b>{item?.ram && humanReadableSize(item?.ram * 1024 * 1024)}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              Disk: <b>{item?.disk && humanReadableSize(item?.disk * 1024 * 1024 * 1024)}</b>
              {disabled && ' (Too Small)'}
            </Text>
          </div>
        </SelectableCard>
      )
    },
    [
      classes.card,
      classes.cardHeader,
      classes.cardText,
      classes.gpuTag,
      classes.pairs,
      params,
      updateParams,
    ],
  )

  return (
    <div className={classes.fields}>
      <Info className={classes.info}>
        <div className={classes.infoContent}>
          <Text variant="caption1">Flavor Selection</Text>
          <Text variant="body2">
            Choose a flavor that defines the VM’s CPU, memory, disk, and optional GPU settings.
          </Text>
        </div>
      </Info>
      <div />
      <div className={classes.filters}>
        <ToggleSwitchField
          id="enableHotplugging"
          value={params.enableHotplugging}
          onChange={(value) => {
            updateParams({
              enableHotplugging: value,
              flavor: [],
            })
          }}
          label="Hot-plug Compatible"
        />
        <ToggleSwitchField
          id="gpuCompatible"
          value={params.gpuCompatible}
          onChange={(value) => {
            updateParams({
              gpuCompatible: value,
              flavor: [],
            })
          }}
          label="GPU Compatible"
        />
      </div>
      {isVolumeSourceType && (
        <Alert
          variant="primary"
          title="Supported Flavors"
          message="Only zero-disk flavors are supported for volume-backed instances, as the root disk comes from the attached volume. As a result, the VM disk size will be equal to the volume size"
        />
      )}
      {viewFlavorToggle === 'grid' && (
        <PaginatedSelectableCards
          renderSelectableCardComponent={renderFlavorCard}
          data={filteredFlavors}
          searchProps={['name']}
          pluralLabel="Flavors"
          extraHeaderContent={
            <ViewModeToggle value={viewFlavorToggle} onChange={setViewFlavorToggle} />
          }
          sortDropdown={
            <Dropdown
              name="sort"
              value={flavorSort}
              items={[
                {
                  label: 'Select...',
                  value: '',
                },
                {
                  label: 'Name',
                  value: 'name',
                },
                {
                  label: 'vCPUs',
                  value: 'vcpus',
                },
                {
                  label: 'RAM',
                  value: 'ram',
                },
                {
                  label: 'Disk',
                  value: 'disk',
                },
              ]}
              onChange={(value) => setFlavorSort(value)}
            />
          }
        />
      )}
      {viewFlavorToggle === 'list' && (
        <>
          <div className={classes.spaceBelow}>
            <ViewModeToggle value={viewFlavorToggle} onChange={setViewFlavorToggle} />
          </div>
          <ListTableField
            id="flavor"
            data={filteredFlavors}
            loading={loadingFlavors}
            columns={params.gpuCompatible ? flavorColumnsGpu : flavorColumns}
            visibleColumns={params.gpuCompatible ? visibleGpuFlavorColumns : visibleFlavorColumns}
            onChange={(value) =>
              updateParams({
                flavor: value,
              })
            }
            checkboxCond={(flavor) => !isFlavorCompatibleWithImageAndVolume(flavor, params)}
            checkboxCondMessage="Flavor disk size is smaller than the selected image"
            value={params.flavor}
            uniqueIdentifier="id"
            searchTargets={['name']}
            noScroll
            required
            emptyText={
              isVolumeSourceType && filteredFlavors?.length === 0
                ? 'No flavors with zero disk size are available. Please create a zero-disk-size flavor to proceed'
                : 'No data found'
            }
          />
        </>
      )}
      {params.enableHotplugging && (
        <>
          <div className={classes.separator} />
          <div className={classes.halfColumns}>
            <SizeInputWithUnit
              id="hotplugMemory"
              label="Hotplug RAM"
              onChange={(value) =>
                updateParams({
                  hotplugMemory: value,
                })
              }
              value={params.hotplugMemory}
              defaultUnit="MiB"
              min={1}
              info="The memory that will be hotplugged to the VM. Must be less than the maximum."
              validations={[
                hotplugMemoryMiBValidator(
                  Number(params.hotplugMemory),
                  Number(params.hotplugMemoryMax),
                ),
                nonDecimalValueValidator(),
              ]}
              required
              items={ramOptions}
            />
            <SizeInputWithUnit
              id="hotplugMemoryMax"
              label="Hotplug RAM Maximum"
              onChange={(value) =>
                updateParams({
                  hotplugMemoryMax: value,
                })
              }
              value={params.hotplugMemoryMax}
              defaultUnit="MiB"
              min={1}
              info="The maximum amount of RAM that can be hotplugged to the VM."
              validations={[minValueValidator(1), nonDecimalValueValidator()]}
              required
              items={ramOptions}
            />
          </div>
          <div className={classes.halfColumns}>
            <TextField
              id="hotplugVcpu"
              label="Hotplug vCPUs"
              onChange={(value) =>
                updateParams({
                  hotplugVcpu: value,
                })
              }
              value={params.hotplugVcpu}
              type="number"
              min={1}
              info="The number of vCPUs that will be hotplugged to the VM. Must be less than the maximum."
              validations={[hotplugVcpuValidator, nonDecimalValueValidator()]}
              required
            />
            <TextField
              id="hotplugVcpuMax"
              label="Hotplug vCPUs Maximum"
              onChange={(value) =>
                updateParams({
                  hotplugVcpuMax: value,
                })
              }
              value={params.hotplugVcpuMax}
              type="number"
              min={1}
              info="The maximum number of vCPUs that can be hotplugged to the VM."
              validations={[hotplugVcpuMaxValidator, nonDecimalValueValidator()]}
              required
            />
          </div>
        </>
      )}
    </div>
  )
}
